import { useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { ChevronDown, MailPlus, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { cn } from "@/lib/utils";
import { track } from "@/lib/track";
import { useTenant } from "@/hooks/useTenant";
import { Chip } from "@/components/ui/chip";
import { FACET_LABELS } from "@/lib/amenities";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { loadWelcomePrefs, saveWelcomePrefs } from "@/lib/welcomePrefs";
import { useAuth } from "@/hooks/useAuth";
import { setIntent } from "@/lib/onboardingIntent";
import {
  ALERT_COPY,
  areaLabel,
  areaOptionsForTenant,
  BEDROOM_OPTIONS,
  BUDGET_OPTIONS,
  type AlertVariant,
} from "./alertCopy";
import { useEmailCapture } from "./useEmailCapture";

interface AlertSignupProps {
  variant: AlertVariant;
  /** Pre-fills relevant fields (used on /properties from filter state). */
  defaults?: {
    area?: string;
    bedrooms?: number;
    budget?: string;
    /** Amenity labels (renter facets), e.g. the active browse filters. */
    amenities?: string[];
  };
  /** Analytics source id, e.g. "home_hero", "browse_top", "browse_empty". */
  source: string;
  className?: string;
  /** When true, after a successful capture redirect to signup with email prefilled. */
  promptSignupAfter?: boolean;
}

const variantStyles: Record<
  AlertVariant,
  {
    container: string;
    headline: string;
    subheadline: string;
    input: string;
    select: string;
    button: string;
    error: string;
    success: string;
  }
> = {
  hero: {
    container:
      "bg-white/10 backdrop-blur-md border border-white/20 rounded-card p-5 sm:p-6 max-w-md mx-auto text-white",
    headline: "font-serif italic text-xl sm:text-2xl leading-tight text-white",
    subheadline: "text-[13px] text-white/80 mt-2",
    input:
      "h-11 w-full bg-white/95 text-foreground placeholder:text-muted-foreground rounded-xl px-3 text-sm border-0 outline-none focus:ring-2 focus:ring-accent",
    select:
      "h-11 w-full bg-white/95 text-foreground rounded-xl px-3 text-sm border-0 outline-none focus:ring-2 focus:ring-accent",
    button:
      "h-11 w-full bg-accent text-accent-foreground hover:bg-accent/90 rounded-full font-medium",
    error: "text-[12px] text-amber-200 mt-1",
    success: "text-white",
  },
  "properties-top": {
    container:
      "bg-card rounded-card shadow-soft border border-border p-5 mb-6",
    headline: "font-serif text-lg font-medium text-foreground",
    subheadline: "text-[13px] text-muted-foreground mt-1",
    input:
      "h-10 w-full bg-background text-foreground placeholder:text-muted-foreground rounded-xl px-3 text-sm border border-border outline-none focus:ring-2 focus:ring-primary",
    select:
      "h-10 w-full bg-background text-foreground rounded-xl px-3 text-sm border border-border outline-none focus:ring-2 focus:ring-primary",
    button:
      "h-10 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-medium px-5",
    error: "text-[12px] text-destructive mt-1",
    success: "text-foreground",
  },
  "properties-empty": {
    container:
      "bg-card rounded-card shadow-soft border border-border p-8 sm:p-12",
    headline: "font-serif text-2xl font-medium text-foreground",
    subheadline: "text-[14px] text-muted-foreground mt-2",
    input:
      "h-11 w-full bg-background text-foreground placeholder:text-muted-foreground rounded-xl px-3 text-sm border border-border outline-none focus:ring-2 focus:ring-primary",
    select:
      "h-11 w-full bg-background text-foreground rounded-xl px-3 text-sm border border-border outline-none focus:ring-2 focus:ring-primary",
    button:
      "h-11 w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-medium",
    error: "text-[12px] text-destructive mt-1",
    success: "text-foreground",
  },
  footer: {
    container:
      "bg-card rounded-card shadow-soft border border-border p-6 sm:p-8 max-w-md mx-auto",
    headline: "font-serif text-xl font-medium text-foreground",
    subheadline: "text-[13px] text-muted-foreground mt-1",
    input:
      "h-10 w-full bg-background text-foreground placeholder:text-muted-foreground rounded-xl px-3 text-sm border border-border outline-none focus:ring-2 focus:ring-primary",
    select:
      "h-10 w-full bg-background text-foreground rounded-xl px-3 text-sm border border-border outline-none focus:ring-2 focus:ring-primary",
    button:
      "h-10 w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-medium",
    error: "text-[12px] text-destructive mt-1",
    success: "text-foreground",
  },
};

const fieldsByVariant: Record<
  AlertVariant,
  { area: boolean; bedrooms: boolean; budget: boolean; amenities: boolean }
> = {
  // Budget is shown on every variant so the form is consistent across
  // surfaces. Budget is a strong filter signal for the matching pipeline;
  // hiding it on some variants weakens matches without saving much time.
  // Amenities only where a renter is already filtering (the browse page):
  // the chips prefill from the active search.
  hero: { area: true, bedrooms: true, budget: true, amenities: false },
  "properties-top": { area: true, bedrooms: true, budget: true, amenities: true },
  "properties-empty": { area: true, bedrooms: true, budget: true, amenities: true },
  footer: { area: true, bedrooms: true, budget: true, amenities: false },
};

const SUBSCRIBED_KEY = "mst_alert_subscribed";

const AlertSignup = ({ variant, defaults, source, className, promptSignupAfter }: AlertSignupProps) => {
  const copy = ALERT_COPY[variant];
  const styles = variantStyles[variant];
  const fields = fieldsByVariant[variant];
  const navigate = useNavigate();
  const tenant = useTenant();
  const { user } = useAuth();
  // Area dropdown scoped to the active tenant's areas.
  const areaOptions = areaOptionsForTenant(tenant);

  // If the caller doesn't override `area`, pre-select the current tenant's
  // primary area so the form arrives pre-scoped to the city the user is on.
  const mergedDefaults = {
    ...defaults,
    area: defaults?.area ?? tenant.primaryAreaSlug,
  };

  // Hide entirely if the user has already subscribed in any prior session.
  const [alreadySubscribed] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(SUBSCRIBED_KEY) === "1"; } catch { return false; }
  });

  // Defaults are captured on initial mount only, react-hook-form's
  // defaultValues are initialize-once. Filter changes after mount don't
  // re-sync into the form. Acceptable for v1; revisit if conversion data
  // shows users change filters between viewing and submitting.
  const { form, status, serverError, submit } = useEmailCapture({
    source,
    defaults: mergedDefaults,
    onSuccess: (values, meta) => {
      try { window.localStorage.setItem(SUBSCRIBED_KEY, "1"); } catch { /* ignore */ }
      // This entry-point only exists for renters, capture intent so the
      // post-auth router lands them on /properties (not /list-property).
      setIntent("renter");
      // Seed the welcome wizard with whatever they already gave us so the
      // next page never asks for the same field twice.
      saveWelcomePrefs({
        email: values.email,
        captureId: meta.captureId ?? undefined,
        preferred_areas: values.preferred_areas ?? [],
        preferred_bedrooms: values.preferred_bedrooms ? Number(values.preferred_bedrooms) : undefined,
        preferred_types: [],
        preferred_amenities: values.preferred_amenities ?? [],
      });
      if (promptSignupAfter) {
        // Soft transition: short pause so the success microcopy is visible,
        // then a router navigation (no full reload, no white flash).
        setTimeout(() => navigate("/welcome"), 700);
      }
    },
  });

  // One-shot view event when the component first scrolls into view.
  const containerRef = useRef<HTMLDivElement>(null);
  const [viewed, setViewed] = useState(false);
  useEffect(() => {
    if (viewed || !containerRef.current) return;
    const node = containerRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          track("alert_signup_view", { source, variant });
          setViewed(true);
          io.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [source, variant, viewed]);

  // Move keyboard focus to the success heading on state transition so screen
  // readers and keyboard users land on the confirmation.
  const successHeadingRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (status === "success") {
      successHeadingRef.current?.focus();
    }
  }, [status]);

  if (alreadySubscribed) {
    // Captured their email but never finished the wizard/account: leave a way
    // back in instead of vanishing forever (signed-in users need nothing).
    const stored = loadWelcomePrefs();
    if (!user && stored?.email) {
      return (
        <div ref={containerRef} className={cn(styles.container, className)}>
          <button
            type="button"
            onClick={() => navigate("/welcome")}
            className="w-full flex items-center justify-between gap-3 text-left text-sm"
          >
            <span className="flex items-center gap-2 min-w-0">
              <MailPlus className="h-4 w-4 text-primary shrink-0" />
              <span className="truncate">Alerts set for {stored.email}. Finish your renter profile</span>
            </span>
            <span className="text-primary font-medium shrink-0">Continue →</span>
          </button>
        </div>
      );
    }
    return null;
  }

  if (status === "success") {
    return (
      <div ref={containerRef} className={cn(styles.container, className)}>
        <div className="flex items-center gap-3">
          <MailPlus className={cn("h-5 w-5 shrink-0", styles.success)} />
          <p
            ref={successHeadingRef}
            tabIndex={-1}
            role="status"
            aria-live="polite"
            className={cn("font-serif text-lg font-medium outline-none", styles.success)}
          >
            {copy.successHeadline}
          </p>
        </div>
        {copy.successBody && (
          <p className={cn("text-[13px] mt-1", styles.subheadline)}>{copy.successBody}</p>
        )}
      </div>
    );
  }

  const emailError = form.formState.errors.email?.message;

  return (
    <div ref={containerRef} className={cn(styles.container, className)}>
      <div className="flex items-start gap-3">
        <MailPlus className="h-5 w-5 shrink-0 mt-0.5" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <h3 className={styles.headline}>{copy.headline}</h3>
          {copy.subheadline && <p className={styles.subheadline}>{copy.subheadline}</p>}
        </div>
      </div>

      <form
        onSubmit={submit}
        noValidate
        className="mt-4 space-y-3"
        aria-label="Email alert signup"
      >
        <div>
          <label htmlFor={`alert-email-${variant}`} className="sr-only">
            Email address
          </label>
          <input
            id={`alert-email-${variant}`}
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@email.com"
            className={styles.input}
            aria-invalid={!!emailError}
            aria-describedby={emailError ? `alert-email-error-${variant}` : undefined}
            {...form.register("email")}
          />
          {emailError && (
            <p id={`alert-email-error-${variant}`} className={styles.error}>
              {emailError}
            </p>
          )}
        </div>

        {(fields.area || fields.bedrooms || fields.budget) && (
          <div
            className={cn(
              "grid gap-2",
              fields.budget ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2",
            )}
          >
            {fields.area && (
              <Controller
                control={form.control}
                name="preferred_areas"
                render={({ field }) => {
                  const selected = field.value ?? [];
                  const labelText =
                    selected.length === 0
                      ? "Any area"
                      : selected.length === 1
                        ? areaLabel(selected[0], tenant)
                        : `${selected.length} areas`;
                  return (
                    <div>
                      <label htmlFor={`alert-area-${variant}`} className="sr-only">
                        Preferred areas
                      </label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            id={`alert-area-${variant}`}
                            type="button"
                            className={cn(styles.select, "flex items-center justify-between text-left")}
                          >
                            <span className={selected.length === 0 ? "text-muted-foreground" : ""}>
                              {labelText}
                            </span>
                            <ChevronDown className="h-4 w-4 opacity-60" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-60 p-1.5 max-h-72 overflow-y-auto" align="start">
                          {areaOptions.map((a) => {
                            const checked = selected.includes(a.value);
                            return (
                              <button
                                key={a.value}
                                type="button"
                                onClick={() => {
                                  if (checked) field.onChange(selected.filter((v) => v !== a.value));
                                  else field.onChange([...selected, a.value]);
                                }}
                                aria-pressed={checked}
                                className={`flex w-full items-center justify-between gap-2 px-3 py-2.5 rounded-input text-sm text-left transition-colors ${
                                  checked ? "bg-primary/10 text-foreground" : "hover:bg-muted text-foreground"
                                }`}
                              >
                                <span className="truncate">{a.label}</span>
                                {checked && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
                              </button>
                            );
                          })}
                        </PopoverContent>
                      </Popover>
                    </div>
                  );
                }}
              />
            )}
            {fields.bedrooms && (
              <div>
                <label htmlFor={`alert-bedrooms-${variant}`} className="sr-only">
                  Bedrooms
                </label>
                <select
                  id={`alert-bedrooms-${variant}`}
                  className={styles.select}
                  {...form.register("preferred_bedrooms")}
                >
                  <option value="">Bedrooms</option>
                  {BEDROOM_OPTIONS.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {fields.budget && (
              <div>
                <label htmlFor={`alert-budget-${variant}`} className="sr-only">
                  Budget
                </label>
                <select
                  id={`alert-budget-${variant}`}
                  className={styles.select}
                  {...form.register("preferred_budget")}
                >
                  <option value="">Budget</option>
                  {BUDGET_OPTIONS.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
        {fields.amenities && (
          <Controller
            control={form.control}
            name="preferred_amenities"
            render={({ field }) => {
              const selected = field.value ?? [];
              return (
                <div className="mt-2">
                  <span className="sr-only">Must-have amenities</span>
                  <div className="flex flex-wrap gap-1.5">
                    {FACET_LABELS.map((label) => (
                      <Chip
                        key={label}
                        label={label}
                        className="w-auto"
                        checked={selected.includes(label)}
                        onChange={() =>
                          field.onChange(selected.includes(label) ? selected.filter((v) => v !== label) : [...selected, label])
                        }
                      />
                    ))}
                  </div>
                </div>
              );
            }}
          />
        )}

        {serverError && (
          <p className={styles.error} role="alert">
            {serverError}
          </p>
        )}

        <Button
          type="submit"
          disabled={status === "submitting"}
          className={styles.button}
        >
          {status === "submitting" ? "Sending…" : copy.cta}
        </Button>
      </form>
    </div>
  );
};

export default AlertSignup;
